import React, { useState } from 'react';
import { useAuth } from '../../../../AuthContext';
import { useProfileSheet } from '../../../../contexts/ProfileSheetContext';
import { validatePassword } from '../../../../utils/passwordValidation';
import SheetField from '../shared/SheetField';
import SheetMessage from '../shared/SheetMessage';
import PasswordStrengthMeter from '../shared/PasswordStrengthMeter';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RegisterView: React.FC = () => {
  const { register } = useAuth();
  const { setView } = useProfileSheet();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [k: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);

  const validate = (): boolean => {
    const errs: { [k: string]: string } = {};
    if (!username.trim()) errs.username = 'Username is required';
    else if (username.trim().length < 3) errs.username = 'At least 3 characters';
    if (!EMAIL_RE.test(email.trim())) errs.email = 'Enter a valid email address';
    const pwErr = validatePassword(password);
    if (pwErr) errs.password = pwErr;
    if (confirm !== password) errs.confirm = 'Passwords do not match';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setError(null);
    setSubmitting(true);
    try {
      await register({
        username: username.trim(),
        email: email.trim(),
        password,
        full_name: fullName.trim() || undefined,
      });
      // Success handling lives in ProfileSheet's auth-success effect.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {error && <SheetMessage kind="err">{error}</SheetMessage>}
      <SheetField
        label="Username"
        value={username}
        onChange={setUsername}
        name="username"
        autoComplete="username"
        error={fieldErrors.username}
        disabled={submitting}
        autoFocus
      />
      <SheetField
        label="Email"
        value={email}
        onChange={setEmail}
        type="email"
        name="email"
        autoComplete="email"
        error={fieldErrors.email}
        disabled={submitting}
      />
      <SheetField
        label="Full name (optional)"
        value={fullName}
        onChange={setFullName}
        name="full_name"
        autoComplete="name"
        disabled={submitting}
      />
      <SheetField
        label="Password"
        value={password}
        onChange={setPassword}
        type="password"
        name="new-password"
        autoComplete="new-password"
        error={fieldErrors.password}
        disabled={submitting}
      />
      <PasswordStrengthMeter password={password} />
      <SheetField
        label="Confirm password"
        value={confirm}
        onChange={setConfirm}
        type="password"
        name="confirm-password"
        autoComplete="new-password"
        error={fieldErrors.confirm}
        disabled={submitting}
      />
      <button
        type="submit"
        className="tend-btn tend-btn--cta tend-btn--block"
        disabled={submitting}
        style={{ marginTop: 4 }}
      >
        {submitting ? 'Creating account…' : 'Create Account'}
      </button>
      <div className="tend-links-row">
        <button type="button" className="tend-link" onClick={() => setView('login')}>
          Already have an account? Sign in
        </button>
      </div>
    </form>
  );
};

export default RegisterView;
